import React, { useEffect, useState } from "react";
import { Form, Input, Select, message } from "antd";
import FormModal from "../../../components/FormModal";
import PasswordFields from "../../../components/PasswordFields";
import { extractMsg } from "../../../utils/errorUtils";

const ROLE_OPTIONS = [
  { label: "User", value: "User" },
  { label: "Admin", value: "Admin" },
  { label: "Super User", value: "Super User" },
];

export function AddUserDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to add user"));
      console.error("Failed to add user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New Skeyeform User"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      initialValues={{ roleLabel: "User" }}
    >
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input placeholder="Enter email address" />
      </Form.Item>
      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, min: 6, max: 24 }]}
      >
        <Input placeholder="Enter username" />
      </Form.Item>
      <PasswordFields />
      <Form.Item name="roleLabel" label="Role" rules={[{ required: true }]}>
        <Select options={ROLE_OPTIONS} placeholder="Select a role" />
      </Form.Item>
    </FormModal>
  );
}

export function EditUserDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({ username: user.username, email: user.email });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to edit user"));
      console.error("Failed to edit user", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Edit User Info"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, min: 6, max: 24 }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input type="email" />
      </Form.Item>
    </FormModal>
  );
}

export function EditRoleDialog({
  open,
  user,
  currentRoleLabel,
  onClose,
  onSave,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({ roleLabel: currentRoleLabel || "User" });
    }
  }, [open, user, currentRoleLabel, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      onClose();
    } catch (error) {
      console.error("Failed to edit role", error);
      message.error(extractMsg(error, "Failed to edit role"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title={`Edit Role: ${user?.username}`}
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Save Configuration"
    >
      <Form.Item
        name="roleLabel"
        label="User Role"
        rules={[{ required: true }]}
      >
        <Select options={ROLE_OPTIONS} placeholder="Select a role" />
      </Form.Item>
    </FormModal>
  );
}
